import { Link, Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { Drawer, Image, Layout, Menu, notification, Tooltip } from "antd";
import PropTypes from "prop-types";
import LoggedInUserMenu from "../organisms/Navbar/LoggedInUserMenu";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { userRequest, userReset } from "@/features/user/duck/user";

import DashboardIcon from "../../assets/svg/DashboardIcon";
import LocationTick from "../../assets/svg/LocationIcon";
import PropertyIcon from "../../assets/svg/PropertyIcon";
import SmsTracking from "../../assets/svg/SmsTrackingIcon";
import GearIcon from "../../assets/svg/GearIcon";
import LogoutIcon from "../../assets/svg/logout";
import FavTabHeartOutlineIcon from "@/assets/svg/FavTabHeartOutlineIcon";

import { currencyRateListRequest, logout } from "@/features/auth/duck/auth";
import { useLocation, matchRoutes, useParams } from "react-router-dom";
import { propertyRoutesPrivate } from "@/features/property/propertyRoute";
import { userRoutesPrivate } from "@/features/user/userRoute";
import BackArrowIcon from "../../assets/svg/BackArrow";
import {
  expandListPlanRequest,
  featurePlanRequest
} from "@/features/property/duck/subscription";
import seoData from "@/data/seoData";
import SeoHelmet from "../organisms/SEO";
import { CurrencyDropdown, LanguageDropdown } from "..";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hook/useLanguage";
import { MenuOutlined } from "@ant-design/icons";
import {
  getPaymentCancelMessage,
  getPaymentSuccessMessage
} from "@/constants/miscConstants";
import { getConfig } from "@/get-config";
import { resetAiChatBot } from "@/features/aiAgentChatBot/duck/aiChatBot";

const { Header, Sider } = Layout;
const { cdnAssetUrl } = getConfig();

const Sidebar = () => {
  // eslint-disable-next-line no-unused-vars
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const onClick = (e) => {
    if (e.key !== "logout") navigate(e.key);
    if (e.key === "logout") {
      dispatch(logout());
      dispatch(resetAiChatBot());
      dispatch(userReset());
    }
  };

  const items = [
    {
      key: "dashboard",
      label: t("common.dashboard"),
      icon: <DashboardIcon />
    },
    {
      key: "add-property",
      label: t("header.addProperty"),
      icon: <LocationTick />,
      style: { gap: "10px" }
    },
    {
      key: "my-properties",
      label: t("common.myProperties"),
      icon: <PropertyIcon />
    },
    {
      key: "inbox",
      label: t("common.inbox"),
      icon: <SmsTracking />
    },
    {
      key: "favorite",
      label: t("common.favorite"),
      icon: <FavTabHeartOutlineIcon />,
      style: { gap: "10px" }
    },
    {
      key: "profile-settings",
      label: t("header.profileSettings"),
      icon: <GearIcon />
    },
    {
      type: "divider"
    },
    {
      key: "logout",
      label: t("header.logout"),
      icon: <LogoutIcon />
    }
  ];

  return (
    <Sider
      breakpoint="lg"
      width={300}
      onCollapse={(collapsed) => setCollapsed(collapsed)}
      className="!bg-[#32493B] !flex !flex-col left-0 top-0 w-[80px] md:w-[300px] transition-all duration-300">
      <div className="flex flex-col h-full">
        <Link
          to="/"
          className="flex justify-center items-center py-7 lg:py-[16.5px] px-4">
          <Image
            alt="HomesGlobe Logo"
            preview={false}
            className={`transition-all duration-300   mx-auto`}
            src={`${cdnAssetUrl}dashboardWhiteLogo.svg`}
            loading="lazy"
          />
        </Link>
        <Menu
          onClick={onClick}
          style={{ minWidth: 0, flex: "auto" }}
          mode="inline"
          items={items}
          className="flex flex-col flex-1"
        />
      </div>
    </Sider>
  );
};
const routes = [
  ...userRoutesPrivate.children,
  ...propertyRoutesPrivate.children
];
const privateRoutes = routes.map((route) => ({
  ...route,
  path: `/manage/${route.path}` // Ensure all routes have "/manage/" prefix
}));
const AppHeader = () => {
  const { propertyId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigation = useNavigate();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [searchParams] = useSearchParams();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const isPrivateRoute = location.pathname.startsWith("/manage");
  // Find the current matching route
  const matchedRoutes = matchRoutes(privateRoutes, location);

  const routeTitleKey = matchedRoutes?.[0]?.route?.title || "common.dashboard";
  const translatedTitle = t(routeTitleKey);

  const { currentUser, totalAvailableListings } = useSelector(
    (state) => ({
      currentUser: state.user.userInfo,
      totalAvailableListings: state.user.totalAvailableListings
    }),
    shallowEqual
  );

  const authToken = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  const paymentSuccessful = searchParams.get("paymentSuccessful");
  const paymentCancelled = searchParams.get("paymentCancelled");

  useEffect(() => {
    if (paymentSuccessful) {
      notification.success(getPaymentSuccessMessage(t));
    }
    if (paymentCancelled) {
      notification.error(getPaymentCancelMessage(t));
    }
    if (paymentSuccessful || paymentCancelled) {
      navigation(
        {
          pathname: location.pathname
        },
        { replace: true }
      );
    }
  }, [paymentSuccessful, paymentCancelled]);

  useEffect(() => {
    if (!currentUser && authToken) {
      dispatch(userRequest({ userId }));
      dispatch(expandListPlanRequest());
      dispatch(featurePlanRequest());
      dispatch(currencyRateListRequest());
    }
  }, [authToken]);

  const handleBackButton = () => {
    return navigation("/manage/favorite");
  };

  const totalListingsText = `${t("myProperties.totalAvailableListings")}: ${
    totalAvailableListings ?? 0
  }`;

  return (
    <>
      <SeoHelmet
        title={seoData.homepage.title}
        description={seoData.homepage.description}
        keywords={seoData.homepage.keywords}
      />
      <Header className="bg-[#32493B] flex justify-between items-center px-6 py-3 h-30 shadow-sm">
        <span
          className={`flex items-center text-white font-manropeBold 
            ${isRTL ? "text-md md:text-lg" : "text-lg md:text-xl"}`}>
          {routeTitleKey === "compareProperties.title" && (
            <button
              className="flex items-center justify-center h-full mr-5"
              onClick={handleBackButton}>
              <BackArrowIcon className="self-center" />
            </button>
          )}
          <span className="self-center">
            {isPrivateRoute &&
              (propertyId ? t("addProperty.editTitle") : translatedTitle)}
          </span>
        </span>

        <nav className="hidden md:flex">
          <ul className="flex items-center justify-center gap-5 text-white">
            {(routeTitleKey === "header.addProperty" ||
              routeTitleKey === "common.myProperties") && (
              <Tooltip title={totalListingsText}>
                <span className="self-center text-[16px] font-bold line-clamp-1">
                  {totalListingsText}
                </span>
              </Tooltip>
            )}
            <CurrencyDropdown className="custom-select-currency" />
            <LanguageDropdown className="custom-select-currency" />
            <LoggedInUserMenu />
          </ul>
        </nav>
        <div
          className="md:hidden cursor-pointer block"
          onClick={() => setDrawerOpen(true)}>
          <MenuOutlined className="text-white text-2xl" />
        </div>

        <Drawer
          placement="right"
          rootClassName="md:!hidden"
          className="home-mobile-menu"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}>
          <nav className="p-4 space-y-4 list-none bg-gradient-to-r !from-[#5e8260] !to-[#466150]">
            {(routeTitleKey === "header.addProperty" ||
              routeTitleKey === "common.myProperties") && (
              <Tooltip title={totalListingsText}>
                <span className="m-2 self-center text-[16px] font-bold text-white">
                  {totalListingsText}
                </span>
              </Tooltip>
            )}
            <div className="flex justify-start items-center gap-4">
              <LoggedInUserMenu />
            </div>
            <div className="flex justify-start items-center gap-4">
              <CurrencyDropdown />
              <LanguageDropdown />
            </div>
          </nav>
        </Drawer>
      </Header>
    </>
  );
};

const LayoutWrapper = () => (
  <Layout className="min-h-screen">
    <Sidebar />
    <Layout>
      <AppHeader />
      <div>
        <Outlet />
      </div>
    </Layout>
  </Layout>
);
LayoutWrapper.propTypes = {
  children: PropTypes.node.isRequired
};

export default LayoutWrapper;
